/**
 * Which status-group series the Overview charts actually draw.
 *
 * Finding A1 of the Overview accuracy review: the builder counts four sections
 * (`live / screening / origination / pending`) but `stage_mapping` maps every live stage to `live`
 * or `origination` — so "Screening" and "Pending Pass/Hold" have been dead legend entries on
 * Pipeline by Sector and Pipeline Composition. The fix is deliberately DATA-DRIVEN rather than a
 * deletion: the two series come back by themselves the day a stage maps to them again, because the
 * mapping is data (`stage_mapping.csv` + DB), not code.
 *
 * `live` and `origination` are always drawn — an empty pipeline should still show its legend.
 */

/** Stack order, least-advanced last, matching what both charts have always drawn. */
const SECTION_ORDER = ['live', 'screening', 'origination', 'pending'] as const;

export type CommittedSection = (typeof SECTION_ORDER)[number];

/** Drawn whatever the data says; the others must earn their place. */
const ALWAYS_DRAWN: readonly string[] = ['live', 'origination'];

/** The candidate (D8 proposed) series. Never derived from the data — the page toggle owns it. */
export const PROPOSED_SECTION = 'proposed';

/**
 * ⚠ The ONE definition of every series label, for both charts and both legends.
 *
 * The candidate band reads "AI Proposed" since ovw-2 (owner request 2026-09-20): the rows are what
 * the classifier proposed, and "Candidate" never said who proposed them. This is user-facing text
 * ONLY — the payload field, the `proposed` section bucket, the localStorage key and every code
 * identifier keep the names they have.
 */
export const SECTION_LABELS: Record<string, string> = {
  live: 'Live',
  screening: 'Screening',
  origination: 'Origination',
  // A8: the two charts disagreed ("Pending" vs "Pending Pass/Hold") about the same series.
  pending: 'Pending Pass/Hold',
  [PROPOSED_SECTION]: 'AI Proposed',
};

/**
 * The committed sections to draw for `rows`, in stack order. A conditional section appears only
 * when at least one row carries a non-zero value for it.
 */
export function committedSections(
  rows: ReadonlyArray<Partial<Record<CommittedSection, number>>>,
): CommittedSection[] {
  return SECTION_ORDER.filter(
    (section) => ALWAYS_DRAWN.includes(section) || rows.some((row) => (row[section] ?? 0) !== 0),
  );
}
